import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type Table as TanStackTable,
} from '@tanstack/react-table'
import { ChevronRightIcon, SearchXIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { AppError } from '@/shared/api'
import { cn } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { EmptyState } from '@/shared/ui/empty-state'
import { ErrorState } from '@/shared/ui/error-state'
import { Pagination } from '@/shared/ui/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select'
import { Skeleton } from '@/shared/ui/skeleton'

/**
 * Per-column presentation. Everything here is layout, not data — the design
 * system decides that numbers sit right and are mono, and the column says which
 * of its values are numbers. Column *width* is TanStack's own `size`.
 */
export type DataTableColumnMeta = {
  align?: 'left' | 'right'
  /** Identifiers, amounts, timestamps and percentages. */
  isMono?: boolean
  /**
   * Keeps the header out of sight but not out of the accessibility tree.
   *
   * A column of row actions has nothing to write above it, and writing nothing
   * leaves an empty `<th>` — which reads as an unnamed column and is a WCAG
   * failure of its own. Name it, then hide the name.
   */
  hideHeader?: boolean
}

/**
 * Row heights are a closed set, like control heights. The height goes on the
 * cells, not the row: a `<tr>` sizes to its content and ignores its own height.
 */
const DENSITY = {
  compact: '[&>td]:h-10.5',
  medium: '[&>td]:h-12',
  spacious: '[&>td]:h-14',
} as const

type DataTableProps<TData, TValue> = {
  data: TData[]
  /**
   * Defined outside the component or produced by a factory called once.
   * Rebuilding this array every render defeats TanStack Table.
   */
  columns: ColumnDef<TData, TValue>[]
  /** True only when there is no data at all yet — never bind this to `isFetching`. */
  isPending?: boolean
  error?: AppError | null
  onRetry?: () => void
  emptyTitle?: string
  /** Shown when a filter leaves nothing — a different situation from no data at all. */
  noMatchTitle?: string
  noMatchDescription?: string
  /**
   * Required. Without it rows are keyed by index, which breaks selection and
   * breaks upserts arriving from a stream.
   */
  getRowId: (row: TData) => string
  onRowClick?: (row: TData) => void
  /** Prefetch the row's detail on intent. */
  onRowHover?: (row: TData) => void
  density?: keyof typeof DENSITY
  isSelectable?: boolean

  /*
   * State. Every slice is uncontrolled until its `on…Change` is supplied, at
   * which point the caller owns it — that is how sorting and paging move into
   * URL search params without the table knowing anything about routing.
   */
  sorting?: SortingState
  onSortingChange?: (sorting: SortingState) => void
  rowSelection?: RowSelectionState
  onRowSelectionChange?: (selection: RowSelectionState) => void
  globalFilter?: string
  onGlobalFilterChange?: (filter: string) => void
  pagination?: PaginationState
  onPaginationChange?: (pagination: PaginationState) => void
  initialPageSize?: number

  /**
   * Total rows across all pages. Required in the default server-driven mode —
   * the range label and the page count come from it.
   */
  rowCount?: number
  /**
   * Sort, filter and slice in the browser instead of on the server. Only for
   * small static sets — roughly 200 rows. Sorting one fetched page while
   * pretending to sort the whole dataset is a correctness bug, not an
   * optimisation.
   */
  isClientSide?: boolean

  /** Hides the built-in pagination — for a table that shows everything. */
  hasPagination?: boolean
  /**
   * Rows-per-page choices, e.g. `[25, 50, 100]`. Renders a picker at the
   * footer's left edge; changing it returns to the first page, because page 7
   * of fifty-row pages is not page 7 of a hundred-row ones.
   */
  pageSizeOptions?: number[]

  /**
   * Filters, search and bulk actions. Receives the table instance so the
   * toolbar can read the selection or drive the global filter without the
   * caller mirroring that state.
   */
  renderToolbar?: (table: TanStackTable<TData>) => ReactNode
  /** Present makes rows expandable; returns the panel's contents. */
  renderExpanded?: (row: TData) => ReactNode
  /**
   * Below 640 the table collapses into this per row — a `RecordCard` carrying
   * the id, the status and the amount. Without it the table just scrolls
   * sideways on a phone.
   */
  renderCard?: (row: TData) => ReactNode
  className?: string
}

export function DataTable<TData, TValue>({
  data,
  columns,
  isPending = false,
  error = null,
  onRetry,
  emptyTitle,
  noMatchTitle,
  noMatchDescription,
  getRowId,
  onRowClick,
  onRowHover,
  density = 'compact',
  isSelectable = false,
  sorting,
  onSortingChange,
  rowSelection,
  onRowSelectionChange,
  globalFilter,
  onGlobalFilterChange,
  pagination,
  onPaginationChange,
  initialPageSize = 20,
  rowCount,
  isClientSide = false,
  hasPagination = true,
  pageSizeOptions,
  renderToolbar,
  renderExpanded,
  renderCard,
  className,
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation('common')
  /*
   * Fallback state only. The architecture puts page, size, sort and filters in
   * URL search params so a view survives F5 and can be shared by link — pass
   * the matching `on…Change` and the table stops keeping its own copy.
   */
  const [innerSorting, setInnerSorting] = useState<SortingState>([])
  const [innerSelection, setInnerSelection] = useState<RowSelectionState>({})
  const [innerFilter, setInnerFilter] = useState('')
  const [innerPagination, setInnerPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  })
  const [expanded, setExpanded] = useState<ExpandedState>({})

  const isExpandable = renderExpanded !== undefined

  // React Compiler skips memoizing this component: TanStack Table returns
  // functions that cannot be memoized without going stale. Expected, and the
  // reason `columns` must be defined outside the caller's render.
  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => getRowId(row),
    state: {
      sorting: sorting ?? innerSorting,
      rowSelection: rowSelection ?? innerSelection,
      globalFilter: globalFilter ?? innerFilter,
      pagination: pagination ?? innerPagination,
      expanded,
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting ?? innerSorting) : updater
      if (onSortingChange === undefined) setInnerSorting(next)
      else onSortingChange(next)
    },
    onRowSelectionChange: (updater) => {
      const next = typeof updater === 'function' ? updater(rowSelection ?? innerSelection) : updater
      if (onRowSelectionChange === undefined) setInnerSelection(next)
      else onRowSelectionChange(next)
    },
    onGlobalFilterChange: (updater: unknown) => {
      const current = globalFilter ?? innerFilter
      const next =
        typeof updater === 'function' ? (updater(current) as string) : (updater as string)
      if (onGlobalFilterChange === undefined) setInnerFilter(next)
      else onGlobalFilterChange(next)
    },
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater(pagination ?? innerPagination) : updater
      if (onPaginationChange === undefined) setInnerPagination(next)
      else onPaginationChange(next)
    },
    onExpandedChange: setExpanded,
    enableRowSelection: isSelectable,
    getRowCanExpand: () => isExpandable,

    // Server-driven by default: the table renders exactly what it was handed.
    manualSorting: !isClientSide,
    manualFiltering: !isClientSide,
    manualPagination: !isClientSide,
    rowCount,

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    // Slicing rows locally is wrong when the server already paged them.
    getPaginationRowModel: isClientSide ? getPaginationRowModel() : undefined,
  })

  const toolbar = renderToolbar?.(table)
  const toolbarStrip = (toolbar === undefined || toolbar === null) ? null : (
    <div className="flex flex-wrap items-center gap-2.5 border-b border-border-hairline px-4 py-3.5">
      {toolbar}
    </div>
  )

  /**
   * The states before the table has rows still keep the toolbar.
   *
   * With server-side filters it is the only way back: narrow a slice to nothing
   * and the controls that did it would vanish with the rows, leaving the browser
   * back button as the only undo. When there is no toolbar this renders exactly
   * what it used to — a bare state, no frame.
   */
  const shell = (body: ReactNode) =>
    toolbarStrip === null ? (
      body
    ) : (
      <div
        data-slot="data-table"
        className={cn('overflow-hidden rounded-xl border border-border bg-surface-1', className)}
      >
        {toolbarStrip}
        <div className="px-4 py-2">{body}</div>
      </div>
    )

  if (error !== null) {
    return shell(<ErrorState error={error} onRetry={onRetry} className="py-12" />)
  }

  if (isPending) {
    return shell(
      <div className="flex flex-col gap-2">
        {Array.from({ length: 8 }, (_, index) => (
          <Skeleton key={index} className="h-10.5 w-full" />
        ))}
      </div>,
    )
  }

  if (data.length === 0) return shell(<EmptyState title={emptyTitle ?? '—'} />)

  const rows = table.getRowModel().rows
  const columnCount = table.getAllLeafColumns().length + (isSelectable ? 1 : 0)

  const { pageIndex, pageSize } = table.getState().pagination
  const total = table.getRowCount()
  const firstOnPage = total === 0 ? 0 : pageIndex * pageSize + 1
  const lastOnPage = Math.min((pageIndex + 1) * pageSize, total)

  return (
    <div
      data-slot="data-table"
      className={cn('overflow-hidden rounded-xl border border-border bg-surface-1', className)}
    >
      {toolbarStrip}

      {renderCard !== undefined && rows.length > 0 && (
        <div className="flex flex-col gap-2 p-3 sm:hidden">
          {rows.map((row) => (
            <Fragment key={row.id}>{renderCard(row.original)}</Fragment>
          ))}
        </div>
      )}

      {/*
       * Focusable, because it scrolls. Below `sm` the table is wider than the
       * screen and the only ways to move it were a mouse wheel and a finger —
       * with a keyboard the right-hand columns were simply unreachable.
       * `group` rather than `region`: a landmark per table would bury the real
       * ones, and the rule this answers only asks that the box take focus.
       *
       * `relative` is load-bearing, not decoration. `overflow` clips a
       * descendant only when this box is also its containing block, and a
       * statically positioned one is not: an absolutely positioned child then
       * resolves against the page root and escapes the clip entirely. A hidden
       * column header (`hideHeader` renders `sr-only`, which is
       * `position: absolute`) sat at x=975 inside a 1030px-wide table and
       * stretched the *document* to 976px — the whole page gained a horizontal
       * scrollbar, every card slid left, and the dock went off screen, while
       * nothing was painted in the space it opened. Measured on /account, where
       * a wide table and a hidden header meet; `relative` makes this box the
       * containing block, so such a child scrolls with the table instead.
       */}
      <div
        tabIndex={0}
        role="group"
        aria-label={t('ui.tableScroll')}
        className={cn('relative overflow-x-auto', renderCard !== undefined && 'max-sm:hidden')}
      >
        <table className="w-full text-body">
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id} className="h-9.5 border-b border-border-hairline bg-black/16">
                {isSelectable && (
                  <th scope="col" className="w-9.5 pl-4">
                    <Checkbox
                      checked={table.getIsAllRowsSelected()}
                      indeterminate={table.getIsSomeRowsSelected()}
                      onCheckedChange={(checked) => {
                        table.toggleAllRowsSelected(checked === true)
                      }}
                      aria-label={t('ui.selectAll')}
                    />
                  </th>
                )}
                {group.headers.map((header) => {
                  const meta = header.column.columnDef.meta as DataTableColumnMeta | undefined
                  const canSort = header.column.getCanSort()
                  const direction = header.column.getIsSorted()
                  const hasWidth = header.column.columnDef.size !== undefined

                  return (
                    <th
                      key={header.id}
                      scope="col"
                      style={hasWidth ? { width: header.getSize() } : undefined}
                      aria-sort={
                        direction === 'asc'
                          ? 'ascending'
                          : direction === 'desc'
                            ? 'descending'
                            : undefined
                      }
                      className={cn(
                        'px-4 text-label font-medium tracking-label text-muted-foreground uppercase',
                        meta?.align === 'right' ? 'text-right' : 'text-left',
                      )}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          // `min-h-6` is the 24px floor WCAG 2.2 puts under a
                          // pointer target. The label's own line box is 17px,
                          // which reads fine and is a third too small to hit.
                          className={cn(
                            'inline-flex min-h-6 items-center gap-1.25 uppercase',
                            // The press belongs to the header, not the row: a
                            // row that reacts takes the whole grid with it.
                            'press cursor-pointer hover:text-foreground',
                          )}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {/* Mono arrows, per the rule that Unicode appears only in
                           * keyboard hints and sort indicators. */}
                          {direction !== false && (
                            <span aria-hidden className="font-mono">
                              {direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </button>
                      ) : (
                        /*
                         * A column that cannot be sorted gets no button. It used
                         * to get a disabled one, which on the actions column —
                         * whose header is empty by nature — produced
                         * `<button></button>`: a control with no accessible name
                         * at all, and the one critical finding in the audit. It
                         * also spent a tab stop on every unsortable column.
                         */
                        <span className={cn('inline-flex items-center', meta?.hideHeader === true && 'sr-only')}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {/* A filter that matches nothing is not an empty table: the toolbar
              * stays put so the query can be undone from where it was typed. */}
            {rows.length === 0 && (
              <tr>
                <td colSpan={columnCount} className="p-4">
                  <EmptyState
                    title={noMatchTitle ?? t('ui.nothingFound')}
                    description={noMatchDescription ?? t('ui.noMatchDescription')}
                    icon={<SearchXIcon strokeWidth={1.5} />}
                    action={
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          table.resetGlobalFilter()
                          table.resetColumnFilters()
                        }}
                      >
                        {t('ui.resetFilters')}
                      </Button>
                    }
                  />
                </td>
              </tr>
            )}

            {rows.map((row) => {
              const isSelected = row.getIsSelected()
              const isOpen = row.getIsExpanded()

              return (
                <Fragment key={row.id}>
                  <tr
                    // The row's identity in the DOM. A detail panel opened from
                    // here has no trigger element to hand focus back to when it
                    // closes — it is opened from a click and driven by the URL —
                    // so it finds the row by this instead.
                    data-row-id={row.id}
                    // Focusable on purpose, but not in the tab order: `-1` lets
                    // the closing panel put focus back exactly where the reader
                    // left it, without adding a stop per row to every table.
                    tabIndex={-1}
                    onClick={() => {
                      if (isExpandable) row.toggleExpanded()
                      onRowClick?.(row.original)
                    }}
                    onMouseEnter={
                      onRowHover === undefined
                        ? undefined
                        : () => {
                            onRowHover(row.original)
                          }
                    }
                    className={cn(
                      DENSITY[density],
                      'border-b border-border-hairline transition-colors hover:bg-surface-2',
                      isSelected && 'bg-primary/6',
                      !isSelected && isOpen && 'bg-surface-2',
                      (isExpandable || onRowClick !== undefined) && 'cursor-pointer',
                    )}
                  >
                    {isSelectable && (
                      <td
                        className="pl-4"
                        onClick={(event) => {
                          event.stopPropagation()
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            row.toggleSelected(checked === true)
                          }}
                          aria-label={t('ui.selectRow')}
                        />
                      </td>
                    )}
                    {row.getVisibleCells().map((cell, index) => {
                      const meta = cell.column.columnDef.meta as DataTableColumnMeta | undefined

                      return (
                        <td
                          key={cell.id}
                          className={cn(
                            'truncate px-4',
                            meta?.isMono === true ? 'font-mono text-caption' : 'text-body',
                            meta?.align === 'right' ? 'text-right text-foreground' : 'text-fg-body',
                          )}
                        >
                          {/* The cell is flex, so `text-right` alone cannot push
                            * content to the edge — the digits of a numeric column
                            * only line up because of this `justify-end`. */}
                          <span
                            className={cn(
                              'flex items-center gap-2.25',
                              meta?.align === 'right' && 'justify-end',
                            )}
                          >
                            {isExpandable && index === 0 && (
                              <ChevronRightIcon
                                aria-hidden
                                strokeWidth={1.8}
                                className={cn(
                                  'size-2.5 shrink-0 text-muted-foreground transition-transform duration-150',
                                  isOpen && 'rotate-90',
                                )}
                              />
                            )}
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </span>
                        </td>
                      )
                    })}
                  </tr>

                  {isOpen && renderExpanded !== undefined && (
                    <tr className="animate-rise border-b border-border-hairline bg-black/20">
                      <td colSpan={columnCount} className="py-4 pr-4 pb-5 pl-15">
                        {renderExpanded(row.original)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* The footer also survives a single page when there is a size picker:
          otherwise a hundred-row choice that fits on one page would hide the
          control that made it, and there would be no way back to twenty-five. */}
      {hasPagination && (table.getPageCount() > 1 || pageSizeOptions !== undefined) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border-hairline px-4 py-3">
          {pageSizeOptions !== undefined && (
            <Select
              items={Object.fromEntries(pageSizeOptions.map((n) => [String(n), String(n)]))}
              value={String(pageSize)}
              onValueChange={(next) => {
                table.setPagination({ pageIndex: 0, pageSize: Number(next) })
              }}
            >
              <SelectTrigger size="sm" aria-label={t('ui.rowsPerPage')} className="w-18">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Pagination
            className="flex-1"
            page={pageIndex}
            pageCount={table.getPageCount()}
            onPageChange={(next) => {
              table.setPageIndex(next)
            }}
            rangeLabel={t('ui.rangeOf', { from: firstOnPage, to: lastOnPage, total })}
          />
        </div>
      )}
    </div>
  )
}
